/**
 * DeleteConfirmDialog Component
 *
 * Confirmation dialog for deleting entries.
 * Shows entry summary and requires user confirmation before deletion.
 */

interface DeleteConfirmDialogProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
	entryDescription: string;
	loading: boolean;
}

export function DeleteConfirmDialog({
	isOpen,
	onClose,
	onConfirm,
	entryDescription,
	loading,
}: DeleteConfirmDialogProps) {
	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-50 overflow-y-auto">
			{/* Backdrop */}
			<div
				className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
				onClick={onClose}
			/>

			{/* Dialog */}
			<div className="flex min-h-full items-center justify-center p-4">
				<div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
					{/* Icon */}
					<div className="pt-6 flex justify-center">
						<div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								className="h-6 w-6 text-red-600"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
								/>
							</svg>
						</div>
					</div>

					{/* Content */}
					<div className="p-6 text-center">
						<h3 className="text-lg font-semibold text-gray-900 mb-2">
							Hapus Catatan
						</h3>
						<p className="text-gray-600 mb-4">
							Apakah kamu yakin ingin menghapus entri ini?
						</p>
						<div className="bg-gray-50 rounded-lg p-3 mb-4">
							<p className="text-sm text-gray-700 font-medium">
								{entryDescription}
							</p>
						</div>
						<p className="text-sm text-gray-500">
							Tindakan ini tidak dapat dibatalkan.
						</p>
					</div>

					{/* Actions */}
					<div className="flex gap-3 p-4 border-t bg-gray-50 rounded-b-lg">
						<button
							type="button"
							onClick={onClose}
							disabled={loading}
							className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium disabled:opacity-50"
						>
							Batal
						</button>
						<button
							type="button"
							onClick={onConfirm}
							disabled={loading}
							className="flex-1 px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition font-medium disabled:opacity-50"
						>
							{loading ? 'Menghapus...' : 'Hapus'}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
